/**
 * Shared video lightbox.
 * Any element with [data-video-lightbox] opens one native <dialog> that holds
 * an iframe for the video in data-video-url. Closing removes the iframe so
 * playback stops, and focus returns to the element that opened the dialog.
 */

let dialog: HTMLDialogElement | null = null;
let opener: HTMLElement | null = null;

/** Builds the dialog once and reuses it for every trigger on the page. */
function getDialog(): HTMLDialogElement {
  if (dialog) {
    return dialog;
  }

  dialog = document.createElement('dialog');
  dialog.className = 'video-lightbox-dialog';
  dialog.innerHTML = `
    <button type="button" class="video-lightbox-close" aria-label="Close video">
      <span aria-hidden="true">&times;</span>
    </button>
    <div class="video-lightbox-inner">
      <div class="video-lightbox-player"></div>
      <div class="video-lightbox-transcript"></div>
    </div>
  `;

  dialog.querySelector('.video-lightbox-close')?.addEventListener('click', () => dialog?.close());

  // A click on the backdrop lands on the dialog element itself
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) {
      dialog?.close();
    }
  });

  dialog.addEventListener('close', () => {
    dialog?.querySelector('.video-lightbox-player')?.replaceChildren();
    dialog?.querySelector('.video-lightbox-transcript')?.replaceChildren();
    document.documentElement.classList.remove('noscroll');
    opener?.focus();
    opener = null;
  });

  document.body.appendChild(dialog);
  return dialog;
}

/** Returns the video URL with autoplay switched on. */
function withAutoplay(url: string): string {
  const parsed = new URL(url, window.location.href);
  parsed.searchParams.set('autoplay', '1');
  return parsed.toString();
}

function openLightbox(trigger: HTMLElement): void {
  const url = trigger.dataset.videoUrl;
  if (!url) {
    return;
  }

  const lightbox = getDialog();
  const title = trigger.dataset.videoTitle || 'Video';

  const iframe = document.createElement('iframe');
  iframe.src = withAutoplay(url);
  iframe.title = title;
  iframe.allow = 'autoplay; fullscreen; picture-in-picture';
  iframe.allowFullscreen = true;

  lightbox.setAttribute('aria-label', title);
  lightbox.querySelector('.video-lightbox-player')?.replaceChildren(iframe);

  const transcriptSource = trigger.dataset.videoTranscript
    ? document.getElementById(trigger.dataset.videoTranscript)
    : null;
  const transcript = lightbox.querySelector('.video-lightbox-transcript');

  if (transcriptSource && transcript) {
    const details = document.createElement('details');
    const summary = document.createElement('summary');
    summary.textContent = 'Transcript';
    details.append(summary);
    details.insertAdjacentHTML('beforeend', transcriptSource.innerHTML);
    transcript.replaceChildren(details);
  }

  opener = trigger;
  document.documentElement.classList.add('noscroll');
  lightbox.showModal();
}

document.addEventListener('click', (event) => {
  const trigger = (event.target as Element).closest<HTMLElement>('[data-video-lightbox]');
  if (trigger) {
    event.preventDefault();
    openLightbox(trigger);
  }
});
